import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import ApplicationHeader from '@rio-cloud/rio-uikit/ApplicationHeader';
import NotificationsContainer from '@rio-cloud/rio-uikit/NotificationsContainer';
import { isMobile, isMobileScreen, SCREEN_LG, SCREEN_SM, toggleZoomOnMobile } from '@rio-cloud/rio-uikit/DeviceUtils';
import useBrand from '@rio-cloud/rio-uikit/useBrand';
import useEffectOnce from '@rio-cloud/rio-uikit/useEffectOnce';
import useWindowResize from '@rio-cloud/rio-uikit/useWindowResize';

import fallbackVersionsList from '../../versionsList.json';
import { version as localUikitVersion } from '../../src/version.js';
import { Themes } from './header/Themes';
import { Loading } from './components/Loading';
import { ApplicationLabel } from './header/ApplicationLabel';
import { VersionSwitcher } from './header/VersionSwitcher';
import { navItems } from './header/Navigation';
import { MenuToggle } from './header/MenuToggle';
import { UikitContext, type UikitVersionsList } from './UikitContext';
import { getStartMenuItems } from './sites/start/StartMenu';
import { getComponentsMenuItems } from './sites/components/ComponentsMenu';
import { getFoundationsMenuItems } from './sites/foundations/FoundationsMenu';
import { getUiTemplatesMenuItems } from './sites/templates/UITemplatesMenu';
import { getUtilitiesMenuItems } from './sites/utilities/UtilitiesMenu';
import { getInternalMenuItems } from './sites/internal/InternalMenu';
import DetachedPreview from './components/playground/DetachedPreview';
import { routes } from './routes';
import { getSelectedUikitVersion } from './utils/getSelectedUikitVersion';
import { getNormalizedEncodedHashPath } from './utils/hashRouting';
import { useTheme } from './hooks/useTheme';

import './uikit.scss';

const Start = lazy(() => import('./sites/start/Start'));
const Foundations = lazy(() => import('./sites/foundations/Foundations'));
const Components = lazy(() => import('./sites/components/Components'));
const UITemplates = lazy(() => import('./sites/templates/UITemplates'));
const Utilities = lazy(() => import('./sites/utilities/Utilities'));
const DemoService = lazy(() => import('./sites/demoService/index'));
const DemoWebsite = lazy(() => import('./sites/demoWebsite/index'));
const Internal = lazy(() => import('./sites/internal/Internal'));
const WidgetDemo = lazy(() => import('./sites/widgets/WidgetDemo'));
const WidgetDemoDialog = lazy(() => import('./sites/widgets/WidgetDemoDialog'));
const PostMessageExample = lazy(() => import('./sites/widgets/PostMessageExample'));
const AppLayoutPreview = lazy(() => import('./sites/components/appLayout/AppLayoutPreview.tsx'));

const RIO_BRAND_LOGO_URL = 'https://uikit.developers.rio.cloud/static/logos/logo_rio_dark.svg';
const RIO_DARK_BRAND_LOGO_URL = 'https://uikit.developers.rio.cloud/static/logos/logo_rio_light.svg';

const isMenuToggleClicked = (event: MouseEvent | TouchEvent) => {
    const target = event.target as HTMLElement;

    const className = target?.className;
    if (typeof className === 'object') {
        return true;
    }

    if (className.includes('UikitMenuToggle')) {
        return true;
    }

    if (className.includes('ActionBarItemIcon')) {
        return (target.firstChild as HTMLElement).className.includes('UikitMenuToggle');
    }
};

const excludeHeaderUrls = [
    'demoService/table',
    'demoWebsite',
    'iframe-widget-example',
    'widgetDemo',
    'widgetDemoDialog',
    'postMessageExample',
    'detached',
];

type UIKITProps = {
    appLayoutPreviewId?: string;
};

export const UIKIT = (props: UIKITProps) => {
    const { appLayoutPreviewId } = props;

    const { hash, search, pathname } = useLocation();
    const navigate = useNavigate();

    const normalizedEncodedHashPath = getNormalizedEncodedHashPath(pathname);

    const isAppLayoutPreview = appLayoutPreviewId !== undefined;

    const { brand } = useBrand();
    const { theme, themeStyle, themeMode, hasDarkmode, onThemeChange } = useTheme();

    const [versionsList, setVersionsList] = useState<UikitVersionsList>(fallbackVersionsList);

    const selectedUikitVersion = getSelectedUikitVersion({
        versionsList,
        documentationVersion: localUikitVersion,
        isDevelopment: import.meta.env.DEV,
    });

    // Keep the responsive navigation in sync across the header and sidebar:
    // - Above SCREEN_LG, the sidebar is displayed in fluid mode and remains open.
    //   The menu toggle is hidden because there is always enough space to display the content.
    // - At SCREEN_LG and below, the sidebar uses fly mode (like before). The menu toggle is added to the header action bar,
    //   and the sidebar is closed when entering this breakpoint and opened again when leaving it.
    // - At SCREEN_SM and below, ApplicationHeader collapses its nav items. On non-mobile devices, those items
    //   are rendered at the top of the sidebar instead so they remain reachable alongside the documentation menu.
    //   This enables easier site navigation without the need for multiple additional clicks.
    const isMobileOrSmallScreen = isMobile() || isMobileScreen();
    const [isApplicationHeaderMobile, setIsApplicationHeaderMobile] = useState(() => window.innerWidth <= SCREEN_SM);
    const [isMenuFlyMode, setIsMenuFlyMode] = useState(() => window.innerWidth <= SCREEN_LG);

    const [showMenu, setShowMenu] = useState(!isMobileOrSmallScreen && !isMenuFlyMode);

    const wasApplicationHeaderMobile = useRef(isApplicationHeaderMobile);
    const wasMenuFlyMode = useRef(isMenuFlyMode);

    const handleWindowResize = useCallback(() => {
        const isApplicationHeaderMobileAfterResize = window.innerWidth <= SCREEN_SM;
        const isMenuFlyModeAfterResize = window.innerWidth <= SCREEN_LG;

        if (wasApplicationHeaderMobile.current !== isApplicationHeaderMobileAfterResize) {
            wasApplicationHeaderMobile.current = isApplicationHeaderMobileAfterResize;
            setIsApplicationHeaderMobile(isApplicationHeaderMobileAfterResize);
        }

        if (wasMenuFlyMode.current !== isMenuFlyModeAfterResize) {
            wasMenuFlyMode.current = isMenuFlyModeAfterResize;
            setIsMenuFlyMode(isMenuFlyModeAfterResize);
            setShowMenu(!isMenuFlyModeAfterResize);
        }
    }, []);

    useWindowResize(handleWindowResize);

    useEffect(() => {
        toggleZoomOnMobile();
    }, []);

    useEffectOnce(() => {
        const abortController = new AbortController();

        const loadVersionsList = async () => {
            try {
                const response = await fetch('/versionsList.json', { signal: abortController.signal });
                if (!response.ok) {
                    return;
                }
                setVersionsList((await response.json()) as UikitVersionsList);
            } catch (error) {
                if (!(error instanceof Error && error.name === 'AbortError')) {
                    return;
                }
            }
        };

        void loadVersionsList();

        return () => abortController.abort();
    });

    useEffect(() => {
        if (!normalizedEncodedHashPath) {
            return;
        }

        navigate({ ...normalizedEncodedHashPath, search }, { replace: true });
    }, [navigate, normalizedEncodedHashPath, search]);

    const handleMenuIconClicked = () => {
        if (showMenu) {
            setShowMenu(false);
        } else {
            setShowMenu(true);
        }
    };

    const handleMenuOutsideClicked = (event: MouseEvent | TouchEvent) => {
        const isMenuIcon = isMenuToggleClicked(event);
        if (!isMenuIcon && showMenu) {
            setShowMenu(false);
        }
    };

    // Don't show the UIKIT ApplicationHeader for certain deep links
    const hasHeader = !excludeHeaderUrls.includes(pathname.replace('/', ''));

    const actionBarItems = [];
    if (import.meta.env.DEV) {
        actionBarItems.push(
            <div
                key='localVersion'
                className='border rounded padding-x-15 padding-y-5 margin-right-10 white-space-nowrap cursor-default'
            >
                v{localUikitVersion}
            </div>
        );
    } else {
        actionBarItems.push(<VersionSwitcher key='versionSwitcher' />);
    }
    // actionBarItems.push(<About key='about' />);
    actionBarItems.push(
        <Themes
            key='themes'
            hasDarkmode={hasDarkmode}
            themeStyle={themeStyle}
            themeMode={themeMode}
            onThemeChange={onThemeChange}
        />
    );

    actionBarItems.push(<MenuToggle key='menuToggle' />);

    if (normalizedEncodedHashPath && hash !== normalizedEncodedHashPath.hash) {
        return null;
    }

    return (
        <UikitContext.Provider
            value={{
                theme,
                themeMode,
                hasDarkmode,
                onThemeChange,
                isApplicationHeaderMobile,
                isMenuFlyMode,
                showMenu,
                onMenuIconClicked: handleMenuIconClicked,
                onMenuOutsideClicked: handleMenuOutsideClicked,
                selectedUikitVersion,
                versionsList,
                startMenu: getStartMenuItems(),
                foundationsMenu: getFoundationsMenuItems(),
                componentsMenu: getComponentsMenuItems(),
                uiTemplateMenu: getUiTemplatesMenuItems(),
                utilitiesMenu: getUtilitiesMenuItems(),
                internalMenu: getInternalMenuItems(),
            }}
        >
            {isAppLayoutPreview ? (
                <Suspense fallback={<Loading />}>
                    <NotificationsContainer />
                    <AppLayoutPreview previewId={appLayoutPreviewId ?? ''} />
                </Suspense>
            ) : (
                <ApplicationLayout className='uikit-demo'>
                    <NotificationsContainer />
                    {hasHeader && (
                        <ApplicationLayout.Header>
                            <ApplicationHeader
                                label={<ApplicationLabel />}
                                homeRoute={<NavLink to='/' />}
                                {...(brand === 'rio'
                                    ? {
                                          brandLogo: RIO_BRAND_LOGO_URL,
                                          darkBrandLogo: RIO_DARK_BRAND_LOGO_URL,
                                      }
                                    : {})}
                                navItems={navItems}
                                actionBarItems={actionBarItems}
                            />
                        </ApplicationLayout.Header>
                    )}
                    <Suspense fallback={<Loading />}>
                        <Routes>
                            {/* Fallback for external links to icons */}
                            <Route path='/components/icons' element={<Navigate to='/foundations/icons' replace />} />

                            <Route path='detached' element={<DetachedPreview />} />
                            <Route path='start/*' element={<Start />} />
                            <Route path='foundations/*' element={<Foundations />} />
                            <Route path='components/*' element={<Components />} />
                            <Route path='utilities/*' element={<Utilities />} />
                            <Route path='templates/*' element={<UITemplates />} />
                            <Route path='demoService/*' element={<DemoService />} />
                            <Route path='demoWebsite' element={<DemoWebsite />} />
                            <Route path={`${routes.INTERNAL}/*`} element={<Internal />} />
                            <Route path='widgetDemo' element={<WidgetDemo />} />
                            <Route path='widgetDemoDialog' element={<WidgetDemoDialog />} />
                            <Route path='postMessageExample' element={<PostMessageExample />} />
                            <Route path='*' element={<Navigate to={`/${routes.START}`} replace />} />
                        </Routes>
                    </Suspense>
                </ApplicationLayout>
            )}
        </UikitContext.Provider>
    );
};
